// services/rtuGroups.js
// RTU 分组配置：多台服务同一空间的 RTU 共享设定值，按各自偏置做简单分级。
//
// 生效配置 = 服务端共享文件 public/rtu-groups.json（所有浏览器一致、持久）
//           叠加 本浏览器覆盖 localStorage['rtuGroupsOverride']（可在页面内编辑）。
// 配置键为 unitId（rtuAdapter 的机组标识：多从机为 `${deviceCode}@${slaveAddress}`，
// 旧模式即 deviceCode —— 旧演示配置无需迁移）。
// 语义：输入值是“组基准值”。应用时，组内每台 RTU（包括被编辑的这台）
// 的该设定值都被设为（基准值 + 各自 offset）。
//
// 传播仅作用于 4 个温度设定值（见 GROUP_SP_VARS）。

const OVERRIDE_KEY = 'rtuGroupsOverride';

// 参与分组传播的温度设定值
export const GROUP_SP_VARS = ['sp_occ_heat', 'sp_occ_cool', 'sp_unocc_heat', 'sp_unocc_cool'];

let fileCache = null;

async function loadFile() {
  if (fileCache) return fileCache;
  try {
    const res = await fetch('/rtu-groups.json', { cache: 'no-store' });
    const json = await res.json();
    fileCache = json.devices || {};
  } catch (e) {
    console.warn('[rtu] rtu-groups.json not available, groups disabled:', e?.message);
    fileCache = {};
  }
  return fileCache;
}

function loadOverride() {
  try {
    return JSON.parse(localStorage.getItem(OVERRIDE_KEY) || '{}');
  } catch {
    return {};
  }
}

/** 生效配置：{ unitId: { group, offset } }（文件 + 本地覆盖）。 */
export async function getGroupConfig() {
  const base = await loadFile();
  const over = loadOverride();
  const merged = {};
  for (const code of new Set([...Object.keys(base), ...Object.keys(over)])) {
    const g = { group: 0, offset: 0, ...(base[code] || {}), ...(over[code] || {}) };
    merged[code] = { group: Number(g.group) || 0, offset: Number(g.offset) || 0 };
  }
  return merged;
}

/** 本浏览器覆盖（原样返回，供编辑器用）。 */
export function getOverride() {
  return loadOverride();
}

/** 保存本浏览器覆盖。config: { unitId: {group, offset} } */
export function saveOverride(config) {
  localStorage.setItem(OVERRIDE_KEY, JSON.stringify(config || {}));
  return config;
}

export function clearOverride() {
  localStorage.removeItem(OVERRIDE_KEY);
}

const withOffset = (v, off) => Math.round((v + off) * 2) / 2;

/**
 * 计算某台 RTU 设定值变更后，需要联动写入的组内其它成员。
 * 输入值为组基准值；成员写入值 = 基准值 + 成员 offset。
 * @param cfg    getGroupConfig() 结果
 * @param unitId 被修改的机组
 * @param writes [{name, value}]（本次应用的所有写入，基准值）
 * @returns [{ unitId, writes:[{name,value}], offset }]（每个联动成员一项）
 */
export function computeGroupWrites(cfg, unitId, writes) {
  const me = cfg[unitId];
  if (!me || !me.group) return [];
  const spWrites = writes.filter((w) => GROUP_SP_VARS.includes(w.name));
  if (!spWrites.length) return [];
  const peers = Object.entries(cfg).filter(
    ([id, c]) => id !== unitId && c.group === me.group
  );
  return peers.map(([id, c]) => ({
    unitId: id,
    offset: c.offset,
    writes: spWrites.map((w) => ({ name: w.name, value: withOffset(w.value, c.offset) })),
  }));
}

/**
 * 被编辑机组自身的写入：温度设定值加自身 offset，其余写入原样。
 */
export function applyOwnOffset(cfg, unitId, writes) {
  const off = cfg[unitId]?.group ? (cfg[unitId]?.offset || 0) : 0;
  if (!off) return writes;
  return writes.map((w) =>
    GROUP_SP_VARS.includes(w.name) ? { name: w.name, value: withOffset(w.value, off) } : w
  );
}
